import axios from 'axios';
import Url from "url-parse";
import { env } from '@/config/config'
import { store } from '@/store';
import authModule from '@/store/modules/auth';

/**
 * Options Exception.
 */
export class OptionsException {
  /**
   * Constructor.
   *
   * @param {String} message
   */
    constructor(message) {
        this.name = "Options Error";
        this.message = message;
    }
}

/**
 * WooCommerce REST API wrapper
 *
 * @param {Object} opt
 */
export default class WooCommerceRestApi {
    /**
     * Class constructor.
     *
     * @param {Object} opt
     */
    constructor(opt) {
        if (!(this instanceof WooCommerceRestApi)) {
            return new WooCommerceRestApi(opt);
        }

        opt = opt || {};

        if (!opt.url) {
            throw new OptionsException("url is required");
        }

        this._setDefaultsOptions(opt);
        this._createAxiosInterceptors(opt.url);
    }

    _createAxiosInterceptors(baseURL) {
        this.securedApi = axios.create({baseURL});

        this.securedApi.interceptors.request.use(async (config) => {
            const token = store.getters.userAuth ? store.getters.userAuth.access_token : null;

            return {
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: `Bearer ${token}`,
                },
            };
        });

        this.securedApi.interceptors.response.use(response => response, error => {
                const originalRequest = error.config;

                if (error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Refresh the token
                  return authModule.doRefreshToken().then((token) => {
                      // Retry the original request with the new token
                      originalRequest.headers.Authorization = `Bearer ${token}`;
                      return axios(originalRequest);
                  }).catch((error) => {
                      authModule.doLogoutUser().finally(() => Promise.reject(error))
                  })
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Set default options
     *
     * @param {Object} opt
     */
    _setDefaultsOptions(opt) {
        this.url = opt.url;
        this.isHttps = /^https/i.test(this.url);
        this.consumerKey = opt.consumerKey || "";
        this.consumerSecret = opt.consumerSecret || "";
        this.encoding = opt.encoding || "utf8";
        this.queryStringAuth = opt.queryStringAuth || false;
        this.port = opt.port || "";
        this.timeout = opt.timeout;
        this.appVersion = env.APP_VERSION;
        this.axiosConfig = opt.axiosConfig || {};
    }

    /**
     * Parse params object.
     *
     * @param {Object} params
     * @param {Object} query
     */
    _parseParamsObject(params, query) {
        for (const key in params) {
            const value = params[key];

            if (typeof value === "object") {
                for (const prop in value) {
                    if (Object.prototype.hasOwnProperty.call(value, prop)) {
                        const itemKey = `${key.toString()}[${prop.toString()}]`;
                        query[itemKey] = value[prop];
                    }
                }
            } else {
                query[key] = value;
            }
        }

        return query;
    }

    /**
     * Normalize query string for oAuth
     *
     * @param  {String} url
     * @param  {Object} params
     *
     * @return {String}
     */
    _normalizeQueryString(url, params) {
        // Exit if don't find query string.
        if (url.indexOf("?") === -1 && Object.keys(params).length === 0) {
            return url;
        }

        const query = new Url(url, null, true).query;
        const values = [];

        let queryString = "";

        // Include params object into URL.searchParams.
        this._parseParamsObject(params, query);

        for (const key in query) {
            if (Object.prototype.hasOwnProperty.call(query, key)) {
                values.push(key);
            }
        }
        values.sort();

        for (const i in values) {
            if (queryString.length) {
                queryString += "&";
            }

            if (Object.prototype.hasOwnProperty.call(values, i)) {
                queryString += encodeURIComponent(values[i])
                    .replace(/%5B/g, "[")
                    .replace(/%5D/g, "]");
                queryString += "=";
                queryString += encodeURIComponent(query[values[i]]);
            }
        }

        return url.split("?")[0] + "?" + queryString;
    }

    /**
     * Get URL
     *
     * @param  {String} endpoint
     * @param  {Object} params
     *
     * @return {String}
     */
    _getUrl(endpoint, params) {
        let url = this.url.slice(-1) === "/" ? this.url : this.url + "/" + endpoint;

        // Include port.
        if (this.port !== "") {
            const hostname = new Url(url).hostname;

            url = url.replace(hostname, hostname + ":" + this.port);
        }

        if (!this.isHttps) {
            return this._normalizeQueryString(url, params);
        }

        return url;
    }

    /**
     * Do requests
     *
     * @param  {String} method
     * @param  {String} endpoint
     * @param  {Object} data
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    _request(method, endpoint, data, params = {}, secured) {
        const url = this._getUrl(endpoint, params);

        const headers = {
            Accept: "application/json"
        };
        // only set "User-Agent" in node environment
        // the checking method is identical to upstream axios
        if (
            typeof process !== "undefined" &&
            Object.prototype.toString.call(process) === "[object process]"
        ) {
            headers["User-Agent"] =
                "RedburgerApp/" + this.appVersion;
        }

        let options = {
            url: url,
            method: method,
            responseEncoding: this.encoding,
            timeout: this.timeout,
            responseType: "json",
            headers
        };

        if (this.isHttps) {
            if (this.consumerKey && this.consumerSecret) {
                if (this.queryStringAuth) {
                    options.params = {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        consumer_key: this.consumerKey,
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        consumer_secret: this.consumerSecret
                    };
                } else {
                    options.auth = {
                        username: this.consumerKey,
                        password: this.consumerSecret
                    };
                }
            }

            options.params = {...options.params, ...params};
        }

        if (data) {
            options.headers["Content-Type"] = "application/json;charset=utf-8";
            options.data = JSON.stringify(data);
        }

        // Allow set and override Axios options.
        options = {...options, ...this.axiosConfig};

        return secured ? this.securedApi(options.url, options) : axios(options);
    }

    /**
     * GET requests
     *
     * @param  {String} endpoint
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    get(endpoint, params = {}, secured = false) {
        return this._request("get", endpoint, null, params, secured);
    }

    /**
     * POST requests
     *
     * @param  {String} endpoint
     * @param  {Object} data
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    post(endpoint, data, params = {}, secured = false) {
        return this._request("post", endpoint, data, params, secured);
    }

    /**
     * PUT requests
     *
     * @param  {String} endpoint
     * @param  {Object} data
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    put(endpoint, data, params = {}, secured = false) {
        return this._request("put", endpoint, data, params, secured);
    }

    /**
     * DELETE requests
     *
     * @param  {String} endpoint
     * @param  {Object} params
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    delete(endpoint, params = {}, secured = false) {
        return this._request("delete", endpoint, null, params, secured);
    }

    /**
     * OPTIONS requests
     *
     * @param  {String} endpoint
     * @param  {Object} params
     * @param  {Boolean} secured
     *
     * @return {Object}
     */
    options(endpoint, params = {}, secured = false) {
        return this._request("options", endpoint, null, params, secured);
    }
}
